/**
 * Phase 13b — per-procedure TanStack Query cache keys.
 *
 * Centralizing the key shapes here keeps cache-invalidation legible
 * (a `useCreateSite` mutation invalidates `sitesKeys.list()` rather
 * than re-deriving the tuple) and makes the discipline reviewable in
 * one place. The pattern mirrors TanStack Query's recommended key-
 * factory shape.
 *
 * Subsequent slices (13c-13h) extend this module by adding new
 * factories alongside the existing ones.
 */

export const sitesKeys = {
  all: ["sites"] as const,
  list: () => ["sites", "list"] as const,
  byId: (siteId: string) => ["sites", "byId", siteId] as const,
};

export const roomsKeys = {
  all: ["rooms"] as const,
  listBySite: (siteId: string) => ["rooms", "listBySite", siteId] as const,
};

export const panelsKeys = {
  all: ["panels"] as const,
  listBySite: (siteId: string) => ["panels", "listBySite", siteId] as const,
  byId: (panelId: string) => ["panels", "byId", panelId] as const,
  contents: (panelId: string) => ["panels", "contents", panelId] as const,
};

/**
 * Phase 13e — devices cache keys for the R-DEVICE-5 duplicate-check
 * modal's pre-commit `findLikelyDuplicates` call.
 *
 * `listBySite` mirrors the rooms / panels factory shape so a future
 * mutation that adds a device (manual create or AI room-scan apply)
 * invalidates the same key the duplicate-check pre-fetch reads.
 */
export const devicesKeys = {
  all: ["devices"] as const,
  listBySite: (siteId: string) => ["devices", "listBySite", siteId] as const,
};

/**
 * Phase 13c — warnings cache keys. `byPanel` is keyed by panel id and
 * the status filter; `open` is the default status the inline-warning
 * surface consumes, but the aggregate Validation surface in 13d wants
 * `dismissed` / `auto-resolved` too without invalidating the open set.
 */
export const warningsKeys = {
  all: ["warnings"] as const,
  byPanel: (panelId: string, status: string = "open") =>
    ["warnings", "byPanel", panelId, status] as const,
};

/**
 * Phase 13g — bottom-up search cache key for the R-SEARCH-2 surface.
 *
 * `bottomUp` is keyed by the trimmed query string; an empty query is
 * a no-op (the search input gates the procedure with `enabled` on a
 * non-empty query). The cache lives for one render — search results
 * are cheap to refetch on the next character — but TanStack's
 * dedupe inside one render run avoids re-firing the procedure when
 * the same query rebuilds.
 */
export const searchKeys = {
  all: ["search"] as const,
  bottomUp: (query: string) => ["search", "bottomUp", query] as const,
};

/**
 * Phase 13g — Reports hub cache key for the Phase 12f
 * `reports.listAvailable` projection. Single-entry, since the
 * projection is tier-scoped (one value per principal). Refetch on
 * upgrade-flow success (13h).
 */
export const reportsKeys = {
  all: ["reports"] as const,
  listAvailable: () => ["reports", "listAvailable"] as const,
};

/**
 * Phase 13h follow-up — `share.*` cache keys for the share-management
 * surface (scenario 46 `share-revoke`). `listMine` is owner-scoped
 * server-side; a single-entry cache mirrors the `reports.listAvailable`
 * pattern. `share.revoke` invalidates this key on 200 so the row
 * unmounts from the panel-detail share-section.
 */
export const shareKeys = {
  all: ["share"] as const,
  listMine: () => ["share", "listMine"] as const,
};

/**
 * Phase 14a — `account.*` cache keys for the Settings → Account
 * surface. `export` is the only read; the cache is single-entry per
 * principal (the procedure is owner-scoped server-side). The export
 * intentionally does not cache by default — the surface fires the
 * query only after the user confirms the export modal — but a key
 * factory keeps the seam shape consistent with the other routers.
 */
export const accountKeys = {
  all: ["account"] as const,
  export: () => ["account", "export"] as const,
};
